import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  AssetCategoryView,
  CreateAssetCategoryInput,
  UpdateAssetCategoryInput,
  getCategories,
  createCategory,
  updateCategory,
  archiveCategory,
} from "../../../../api/General/assets";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/Dialog";
import Button from "@/components/ui/Button";
import AlertDialog from "@/components/ui/AlertDialog";

interface AssetCategoriesTabProps {
  onCategoriesChanged?: () => void;
}

export default function AssetCategoriesTab({ onCategoriesChanged }: AssetCategoriesTabProps) {
  const { t } = useTranslation();

  const [categories, setCategories] = useState<AssetCategoryView[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [formOpen, setFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<AssetCategoryView | null>(null);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Toast
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  // Archive confirmation
  const [archiveTarget, setArchiveTarget] = useState<AssetCategoryView | null>(null);
  const [archiving, setArchiving] = useState(false);

  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getCategories();
      if (result.errors.length === 0) {
        setCategories(result.payload);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const openAdd = () => {
    setEditingCategory(null);
    setName("");
    setDescription("");
    setErrors({});
    setFormOpen(true);
  };

  const openEdit = (cat: AssetCategoryView) => {
    setEditingCategory(cat);
    setName(cat.name);
    setDescription(cat.description ?? "");
    setErrors({});
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingCategory(null);
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = t("assetCategories.nameRequired");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      if (editingCategory) {
        const input: UpdateAssetCategoryInput = {
          id: editingCategory.id,
          name: name.trim(),
          description: description.trim() || undefined,
        };
        const result = await updateCategory(input);
        if (result.errors.length === 0) {
          showToast(t("assetCategories.categoryUpdated"), "success");
          closeForm();
          loadCategories();
          onCategoriesChanged?.();
        } else {
          showToast(result.errors[0]?.description || t("Something went wrong"), "error");
        }
      } else {
        const input: CreateAssetCategoryInput = {
          name: name.trim(),
          description: description.trim() || undefined,
        };
        const result = await createCategory(input);
        if (result.errors.length === 0) {
          showToast(t("assetCategories.categoryCreated"), "success");
          closeForm();
          loadCategories();
          onCategoriesChanged?.();
        } else {
          showToast(result.errors[0]?.description || t("Something went wrong"), "error");
        }
      }
    } catch {
      showToast(t("Something went wrong"), "error");
    } finally {
      setSaving(false);
    }
  };

  // Open the confirmation dialog; the actual call happens in confirmArchive
  const handleArchive = (cat: AssetCategoryView) => {
    setArchiveTarget(cat);
  };

  const confirmArchive = async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      const result = await archiveCategory(archiveTarget.id);
      if (result.errors.length === 0) {
        showToast(t("assetCategories.categoryArchived"), "success");
        loadCategories();
        onCategoriesChanged?.();
      } else {
        showToast(result.errors[0]?.description || t("Something went wrong"), "error");
      }
    } catch {
      showToast(t("Something went wrong"), "error");
    } finally {
      setArchiving(false);
      setArchiveTarget(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-foreground">{t("assetCategories.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("assetCategories.subtitle")}</p>
        </div>
        <Button
          variant="outline"
          onClick={openAdd}
          data-testid="add-category-btn"
        >
          <svg className="w-4 h-4 me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t("assetCategories.addCategory")}
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : categories.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-foreground">{t("assetCategories.noCategories")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t("assetCategories.noCategoriesDescription")}</p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("assetCategories.name")}
                </th>
                <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("assetCategories.description")}
                </th>
                <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("assetCategories.assetCount")}
                </th>
                <th className="px-4 py-3 text-end text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("assetCategories.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {categories.map((cat) => (
                <tr key={cat.id} className="hover:bg-muted/30 transition-colors" data-testid={`category-row-${cat.id}`}>
                  <td className="px-4 py-3 text-sm font-medium text-foreground">
                    {cat.name}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {cat.description || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      {cat.assetCount}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-end">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => openEdit(cat)}
                        data-testid={`edit-category-${cat.id}`}
                      >
                        <svg className="w-3.5 h-3.5 me-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        {t("common.edit")}
                      </Button>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => handleArchive(cat)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`archive-category-${cat.id}`}
                      >
                        <svg className="w-3.5 h-3.5 me-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        {t("assets.archive")}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit Modal */}
      <Dialog open={formOpen} onOpenChange={(o) => !o && closeForm()}>
        <DialogContent size="default">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? t("assetCategories.editCategory") : t("assetCategories.addCategory")}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <DialogBody className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t("assetCategories.name")} *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${errors.name ? "border-destructive" : "border-border"}`}
                  data-testid="category-name-input"
                  autoFocus
                />
                {errors.name && <p className="text-destructive text-xs mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t("assetCategories.description")}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
                  data-testid="category-description-input"
                />
              </div>
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeForm}
                data-testid="category-form-cancel"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                loading={saving}
                data-testid="category-form-save"
              >
                {saving ? t("common.saving") : editingCategory ? t("common.save") : t("assetCategories.addCategory")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation */}
      <AlertDialog
        open={!!archiveTarget}
        onOpenChange={(open) => { if (!open) setArchiveTarget(null); }}
        title={t("assets.archive")}
        description={t("assetCategories.confirmArchive")}
        variant="destructive"
        confirmLabel={t("assets.archive")}
        cancelLabel={t("common.cancel")}
        isLoading={archiving}
        onConfirm={confirmArchive}
      />

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 end-6 z-[60] px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
            toast.type === "success" ? "bg-success/10 text-success border border-success/20" : "bg-destructive/10 text-destructive border border-destructive/20"
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
